import { useEffect, useState } from "react";
import type { ChannelRollup } from "@/lib/channelReconciliation";
import {
  GrowthChannelsPageService,
  type GrowthAttributionJobRow,
  type GrowthAttributionJobsPageQuery,
  type GrowthAttributionSourceRow,
  type GrowthCallCenterRepRow,
  type GrowthCampaignRow,
  type GrowthDispositionQuery,
  type GrowthDispositionRow,
  type GrowthMarketingSummary,
  type GrowthSourcesRow,
  type PageResponse,
} from "@/services/growth-channels-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useGrowthChannelRollups(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<ChannelRollup[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getChannelRollups(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthSources(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<GrowthSourcesRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getSources(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthMarketingSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<GrowthMarketingSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getMarketingSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthAttributionSources(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<GrowthAttributionSourceRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getAttributionSources(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthAttributionJobsPage(query: GrowthAttributionJobsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<GrowthAttributionJobRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getAttributionJobsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [
    query.enabled,
    query.page,
    query.pageSize,
    query.search,
    query.source,
    query.channel,
    query.status,
    query.quality,
    query.branch,
    query.region,
    query.sortKey,
    query.sortDir,
    query.refreshToken,
  ]);

  return { ...state, loading };
}

export function useGrowthCampaigns(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<GrowthCampaignRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getCampaigns(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthCallCenterReps(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<GrowthCallCenterRepRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getCallCenterReps(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useGrowthDispositionSources(query: GrowthDispositionQuery & { enabled?: boolean }) {
  const [data, setData] = useState<GrowthDispositionRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void GrowthChannelsPageService.getDispositionSources(query).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.sourceType, query.dateFrom, query.dateTo, query.refreshToken]);

  return { data, error, loading };
}
